import { AfterViewInit, Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Store } from '@ngxs/store';
import { PerformancePersonalReviewStatusEnum } from 'app/library/performance-personal-review/enums/performance-personal-review-status.enum';
import { PerformancePersonalReviewStatus } from 'app/library/performance-personal-review/models/performance-personal-review-status';
import { PerformanceProfilePersonalReview } from 'app/library/performance-profile-personal-review/models/performance-profile-personal-review.model';
import { PerformanceProfilePersonalReviewService } from 'app/library/performance-profile-personal-review/services/performance-profile-personal-review.service';
import { PerformanceTeamReview } from 'app/library/performance-team-review/models/performance-team-review.model';
import { AssignToTeamDialogComponent } from 'app/library/team/components/dialogs/assign-to-team-dialog/assign-to-team-dialog.component';
import { Team } from 'app/library/team/models/team.model';
import { TeamService } from 'app/library/team/services/team.service';
import { SelectTeamsDialogComponent } from 'app/main/pages/teams/dialogs/select-teams-dialog/select-teams-dialog.component';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { PreformanceProfilePersonalReviewsDataSource } from './datasource';

@Component({
    selector: 'app-performance-profile-personal-reviews-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class PerformanceProfilePersonalReviewsListComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit {
    @Input()
    filteredPerformanceProfilePersonalReviews$: Observable<PerformanceProfilePersonalReview[]>;

    @Input()
    isFetchingPerformanceProfilePersonalReviewsList$: Observable<boolean>;

    @Input()
    performanceProfilePersonalReviewsStateTotalCount$: Observable<number>;

    @Input()
    loadPerformanceProfilePersonalReviews$: Observable<boolean>;

    @Input()
    userId: string;

    @Input()
    canAccessInputPhase: boolean = false;

    @Input()
    canOpenPerformanceReviews: boolean = true;

    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    selectTeamsDialogRef: MatDialogRef<SelectTeamsDialogComponent>;

    dataSource: PreformanceProfilePersonalReviewsDataSource;
    performancePersonalReviewStatus = PerformancePersonalReviewStatus;
    PerformancePersonalReviewStatusEnum = PerformancePersonalReviewStatusEnum;
    displayedColumns = ['name', 'type', 'team', 'status', 'dueDate'];
    profile = this._store.selectSnapshot(UserState.getMyInfo);

    @ViewChild(MatPaginator)
    paginator: MatPaginator;

    constructor(
        private _store: Store,
        private _action$: Actions,
        private _matDialog: MatDialog,
        private _teamService: TeamService,
        private _performanceProfilePersonalReviewService: PerformanceProfilePersonalReviewService
    ) {
        super();
    }

    ngOnInit(): void {
        this.dataSource = new PreformanceProfilePersonalReviewsDataSource(this.filteredPerformanceProfilePersonalReviews$, this._store, this._action$);
        if (this.userId !== this.profile.id && this.canOpenPerformanceReviews) {
            this.displayedColumns.push('buttons');
        }
    }

    ngAfterViewInit(): void {
        this.loadPerformanceProfilePersonalReviews$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((resetPageIndex) => resetPageIndex !== null)
            )
            .subscribe((resetPageIndex) => {
                this.loadPerformanceProfilePersonalReviewsPage(resetPageIndex);
            });

        this.paginator.page.subscribe(({ length, pageIndex, pageSize }: PageEvent) => {
            const itemsleft = length - pageIndex * pageSize;
            const itemsInThisPage = itemsleft < pageSize ? itemsleft : pageSize;
            // FIXME: these hardcoded numbers represent the height of the rest of the elements surrounding the table within the layout content
            const tableHeight = 80 + 24 + 64 + 56 + itemsInThisPage * 65 + 72 + 56;
            const contentElementHeight = document.getElementById('container-2').offsetHeight;
            const scrollTop = tableHeight > contentElementHeight ? tableHeight - contentElementHeight : 0;

            setTimeout(() => {
                document.getElementById('container-3').scrollTop = scrollTop;
            });
        });

        this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.loadPerformanceProfilePersonalReviewsPage(false);
        });
    }

    loadPerformanceProfilePersonalReviewsPage(resetPageIndex = false): void {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        this.dataSource.loadPerformanceProfilePersonalReviews(this.userId, this.paginator.pageSize, this.paginator.pageIndex + 1);
    }

    openPerformanceReviewDetailsPage(performanceProfilePersonalReview: PerformanceProfilePersonalReview): void {
        if (!this.canOpenPerformanceReviews) {
            return;
        }

        let path: string;

        if (this.canAccessInputPhase) {
            path = `profile/performance-review/${performanceProfilePersonalReview.id}`;
        } else if (performanceProfilePersonalReview.status === this.PerformancePersonalReviewStatusEnum.COMPLETED) {
            path = `/users/${this.userId}/performance-review/${performanceProfilePersonalReview.id}`;
        }

        if (path) {
            this._store.dispatch(new Navigate([path]));
        }
    }

    async openMoveToTeamDialog(performanceProfilePersonalReview: PerformanceProfilePersonalReview): Promise<void> {
        await this._teamService.fetchUserTeamsV1(this.userId, 'teamMember');

        const moveToDialog = this._matDialog.open(AssignToTeamDialogComponent, {
            disableClose: false,
            width: '976px',
            autoFocus: false,
        });

        const teams = this._teamService.getTeamMemberTeams();
        const clonedTeams = JSON.parse(JSON.stringify(teams));
        clonedTeams.map((team: Team) => (team.isSelected = false));
        const teamListTree = this._teamService.convertListToTree(clonedTeams);

        moveToDialog.componentInstance.listTree = teamListTree;
        moveToDialog.componentInstance.userId = this.userId;
        moveToDialog.componentInstance.title = 'GENERAL.BUTTON.ASSIGN_TO_NEW_TEAM';
        moveToDialog.componentInstance.performancePersonalReview = performanceProfilePersonalReview;

        moveToDialog.afterClosed().subscribe((result: PerformanceTeamReview) => {
            if (result) {
                this._performanceProfilePersonalReviewService.updatePerformanceProfilePersonalReview(performanceProfilePersonalReview, result.id);
            }

            this.loadPerformanceProfilePersonalReviewsPage(false);
        });
    }
}
